import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useApplyBrand } from "@/hooks/use-apply-brand";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { usePortalBranding } from "@/hooks/use-portal-branding";
import { DEFAULT_BRAND_COLOR } from "@/lib/brand";
import { isPortalSlug } from "@/lib/portal-slug";
import { NeutralFrame } from "@/components/shell/neutral-frame";
import { PortalFrame } from "@/components/shell/portal-frame";
import { Button, Skeleton } from "@/components/ui";

export const Route = createFileRoute("/portal/$slug")({
  component: PortalLayout,
});

/**
 * Every page under a utility's slug: the public branding read decides the
 * frame. An unknown slug renders the neutral page, its one sentence the page's
 * heading, and no child route.
 */
function PortalLayout() {
  const { slug } = Route.useParams();
  const { t } = useTranslation("customer");
  const branding = usePortalBranding(slug);
  const data = branding.data;
  const notFound = !isPortalSlug(slug) || branding.error?.status === 404;
  const neutral = notFound || (!data && branding.isError);

  // The pre-auth brand writer. A neutral page belongs to no utility, so it
  // paints the default over whatever the pre-paint or the remembered branding
  // drew; otherwise it is a no-op until the read (or the remembered
  // placeholder) answers, so the pre-paint stays until then.
  useApplyBrand(neutral ? DEFAULT_BRAND_COLOR : data?.brand_color);
  useDocumentTitle(data?.org_name ?? null);

  if (notFound) {
    return (
      <NeutralFrame>
        <h1 className="max-w-[36ch] text-center text-lg text-ink">
          {t("portal.neutral.instruction")}
        </h1>
      </NeutralFrame>
    );
  }

  if (data) {
    return (
      <PortalFrame branding={data} slug={slug}>
        <Outlet />
      </PortalFrame>
    );
  }

  if (neutral) {
    return (
      <NeutralFrame>
        <section className="w-full max-w-[420px] space-y-3 rounded-card bg-surface p-5 shadow-card lg:p-7">
          <h1 className="text-xl font-semibold tracking-[-0.01em] text-ink">
            {t("portal.loadError.title")}
          </h1>
          <p className="text-sm text-body">{t("portal.loadError.body")}</p>
          <Button variant="secondary" onClick={() => void branding.refetch()}>
            {t("portal.loadError.retry")}
          </Button>
        </section>
      </NeutralFrame>
    );
  }

  return <PortalSkeleton />;
}

/** The frame's shape, still, while the branding is read. */
function PortalSkeleton() {
  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <div aria-hidden="true" className="h-1 shrink-0 bg-brand-fg" />
      <div className="flex flex-1 flex-col lg:grid lg:grid-cols-2">
        <div className="px-4 pt-8 pb-5 lg:flex lg:flex-col lg:justify-center lg:border-r lg:border-line lg:bg-surface lg:px-14 lg:py-12">
          <div className="mx-auto w-full max-w-[420px] space-y-3 lg:mx-0">
            <Skeleton variant="card" width={48} height={48} />
            <Skeleton width="70%" height={26} />
            <Skeleton width="45%" height={16} />
          </div>
        </div>
        <div className="flex flex-1 flex-col px-4 lg:items-center lg:justify-center lg:px-12 lg:py-12">
          <div className="mx-auto w-full max-w-[420px] lg:mx-0">
            <Skeleton variant="card" height={320} />
          </div>
        </div>
      </div>
    </div>
  );
}
